import dayjs from "dayjs";

export const isNil = (v) => v == null || v === "";

export const formatDateTime = (value, format = "DD/MM/YYYY hh:mm A") => {
  if (!value) return "—";

  const d = dayjs(value);
  return d.isValid() ? d.format(format) : "—";
};

export const toLocalInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};
export const formatCurrency = (value, currency = "₹") => {
  if (value === undefined || value === null) return "—";
  const num = Number(value);
  if (isNaN(num)) return "—";

  return `${currency}${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};
export const ColorConvertor = (hex) => {
  hex = hex?.replace("#", "");

  if (hex?.length === 3) {
    hex = hex
      ?.split("")
      ?.map((c) => c + c)
      ?.join("");
  }

  const num = parseInt(hex, 16);
  return `${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}`;
};

export const getFileType = (fileName) => {
  if (!fileName || typeof fileName !== "string") return "unknown";

  const extension = fileName.split(".").pop().toLowerCase();

  if (extension === fileName.toLowerCase()) return "unknown";

  const types = {
    image: ["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp", "tiff", "ico"],
    document: ["pdf", "doc", "docx", "txt", "rtf", "odt"],
    spreadsheet: ["xls", "xlsx", "csv", "ods"],
  };

  for (const [type, extensions] of Object.entries(types)) {
    if (extensions.includes(extension)) {
      return type;
    }
  }

  return "unknown";
};
export const getInitials = (label = "") => {
  const cleanLabel = String(label).replace(/[^a-zA-Z0-9\s]/g, "");
  const words = cleanLabel.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "-";
  if (words.length === 1) return words[0].slice(0, 1).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};
export const formatTime = (dateStr) => {
  if (!dateStr) return "";
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return "just now";
  // was "0d ago" for anything under a day
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export const relativeTime = (dateString) => {
  if (!dateString) return "-";

  const now = new Date();
  const date = new Date(dateString);
  const diff = date.getTime() - now.getTime();
  const absDiff = Math.abs(diff);

  const minutes = Math.floor(absDiff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const plural = (value, unit) => `${value} ${unit}${value !== 1 ? "s" : ""}`;

  // Less than 1 minute
  if (absDiff < 60000) {
    return { text: "Just now" };
  }

  // Future time
  if (diff > 0) {
    if (minutes < 60) {
      return { text: `in ${plural(minutes, "minute")}` };
    }

    if (hours < 24) {
      return { text: `in ${plural(hours, "hour")}` };
    }

    if (days < 7) {
      return { text: `in ${plural(days, "day")}` };
    }

    return {
      text: formatDateTime(dateString, "MMMM D, YYYY, hh:mm A"),
    };
  }

  // Past time
  if (minutes < 60) {
    return { text: `${plural(minutes, "minute")} ago` };
  }

  if (hours < 24) {
    return { text: `${plural(hours, "hour")} ago` };
  }

  // Today
  if (days === 0) {
    return {
      text: `Today, ${formatDateTime(dateString, "hh:mm A")}`,
    };
  }

  if (days < 31) {
    return {
      text: `${plural(days, "day")} ago, ${formatDateTime(
        dateString,
        "hh:mm A"
      )}`,
    };
  }

  return {
    text: formatDateTime(dateString, "MMMM D, YYYY, hh:mm A"),
  };
};

export const getRelativeTime = (dateString, status) => {
  if (!dateString) return { text: "-", color: "" };
  const now = new Date();
  const date = new Date(dateString);
  const diffTime = date.getTime() - now.getTime();
  const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));

  if (status === "completed") {
    return {
      text: formatDateTime(dateString, "DD-MM-YYYY, hh:mm A"),
      color: "text-success",
    };
  }
 if (status === "cancelled") {
  const absDays = Math.abs(Math.floor(diffTime / (1000 * 60 * 60 * 24)));
    return {
      text: absDays === 0 ? "Today" : `${absDays} days ago`,
      color: "text-danger",
    };
  }
  if (status === "overdue") {
    const absDays = Math.abs(Math.floor(diffTime / (1000 * 60 * 60 * 24)));
    return {
      text: absDays === 0 ? "Today" : `${absDays} days ago`,
      color: "text-yellow",
    };
  }

  if (status === "todo") {
    return {
      text: `Due in ${Math.abs(diffHours)} hours`,
      color: "primary-text",
    };
  }

  return {
    text: formatDateTime(dateString, "DD-MM-YYYY, hh:mm A"),
    color: "text-info",
  };
};

/**
 * Navigates back to the previous page, preserving all URL params.
 *
 * Behaviour:
 * - Uses `location.state.from` (the URL to go back to) with `{ replace: true }`
 *   so the current history entry is replaced instead of stacking a new one.
 *   This prevents the details → edit → details → edit loop.
 * - Restores `location.state.fromListing` as the new `from` on the destination
 *   page, so multi-level chains (listing → details → edit → back → back) work
 *   correctly without losing the listing URL.
 * - Falls back to `navigate(-1)` (browser back) when no `from` state exists
 *   (direct links, page refresh).
 *
 * How to set up the state chain in a details page that has an Edit button:
 *   // When navigating TO the edit form:
 *   navigate(`/lead/edit/${id}`, {
 *     state: {
 *       from: location.pathname + location.search, // edit → back to details
 *       fromListing: location.state?.from,         // details → back to listing (passed through)
 *     },
 *   });
 *
 * @param {Function} navigate - React Router navigate function
 * @param {object}   location - React Router location object
 */
export const navigateBack = (navigate, location) => {
  if (location?.state?.from) {
    const nextState = {};
    if (location.state?.fromListing) {
      nextState.from = location.state.fromListing;
    }
    if (location.state?.focusedId) {
      nextState.focusedId = location.state.focusedId;
    }
    navigate(location.state.from, {
      replace: true,
      state: Object.keys(nextState).length > 0 ? nextState : undefined,
    });
  } else {
    navigate(-1);
  }
};